<template>
    <div>
        <kgr-kindergarten-select
            v-model="institution"
            :federation-id="federationId"
            inline
            class="kgr-row"
            @post-input="changeInstitution()"
        />
        <kgr-empty-data
            v-if="institution === null"
            :value="$t('Bitte wählen Sie eine Einrichtung, für die Sie die Bereiche einstellen oder bearbeiten wollen.')"
            class="mt1"
        />
        <kgr-section-relation-data-provider
            v-else
            v-slot="scope"
            :kindergarten-id="institution.id"
        >
            <div>
                <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
                    <thead>
                        <tr class="kgr-table__row">
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('section') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('Start') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('Ende') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="kgr-table__row">
                            <td class="kgr-table__cell">
                                <kgr-section-select
                                    v-model="newRelation.section"
                                    :federation-id="federationId"
                                    hide-label
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-date-picker
                                    v-model="newRelation.start"
                                    :invalid="!newRelation.isEmpty() && (!newRelation.start || !isValidDateRange(newRelation))"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-date-picker
                                    v-model="newRelation.end"
                                    :invalid="!newRelation.isEmpty() && !isValidDateRange(newRelation)"
                                />
                            </td>
                            <td class="kgr-table__cell">
&nbsp;
                            </td>
                        </tr>
                        <tr
                            v-for="sectionRelation in scope.data"
                            class="kgr-table__row"
                        >
                            <td class="kgr-table__cell">
                                {{ sectionRelation.section.name }}
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="isEditable(sectionRelation)">
                                    <kgr-date-picker
                                        v-model="editRelations[getEditIndex(sectionRelation)].start"
                                        :invalid="!isValidDateRange(editRelations[getEditIndex(sectionRelation)])"
                                    />
                                </template>
                                <template v-else>
                                    {{ moment(sectionRelation.start).format('DD.MM.YYYY') }}
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="isEditable(sectionRelation)">
                                    <kgr-date-picker
                                        v-model="editRelations[getEditIndex(sectionRelation)].end"
                                        :invalid="!isValidDateRange(editRelations[getEditIndex(sectionRelation)])"
                                    />
                                </template>
                                <template v-else>
                                    {{ sectionRelation.formattedEnd() }}
                                </template>
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--actions">
                                <kgr-icon-edit
                                    :size="15"
                                    :disabled="isEditable(sectionRelation)"
                                    class="kgr-table__action"
                                    @click="edit(sectionRelation)"
                                />
                                <kgr-icon-delete
                                    class="kgr-table__action"
                                    @click="remove(sectionRelation, scope.reload, scope.remove)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <kgr-button
                    class="button_save"
                    :value="$t('saveChanges')"
                    :disabled="!isSavable()"
                    :pending="pendingCreate || pendingUpdate"
                    @click="save(scope.reload)"
                />
            </div>
        </kgr-section-relation-data-provider>
    </div>
</template>

<script>
import DatePicker from '../common/form/picker/DatePicker';
import EmptyData from '../common/EmptyData';
import IconDelete from '../common/icon/IconDelete';
import IconEdit from '../common/icon/IconEdit';
import KgrButton from '../common/form/button/KgrButton';
import KindergartenSelect from '../common/form/select/KindergartenSelect';
import SectionRelation from '../../js/dto/SectionRelation';
import SectionRelationDataProvider from '../data-provider/SectionRelationDataProvider';
import SectionRelationRepository from '../../js/repository/SectionRelationRepository';
import SectionSelect from '../common/form/select/SectionSelect';

export default {
    components: {
        'kgr-section-select': SectionSelect,
        'kgr-kindergarten-select': KindergartenSelect,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-date-picker': DatePicker,
        'kgr-empty-data': EmptyData,
        'kgr-section-relation-data-provider': SectionRelationDataProvider,
    },
    props: {
        federationId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            institution: null,
            newRelation: new SectionRelation(),
            editRelations: [],
            pendingCreate: false,
            pendingUpdate: false,
        }
    },
    methods: {
        isEditable(sectionRelation) {
            return this.getEditIndex(sectionRelation) !== -1;
        },
        getEditIndex(sectionRelation) {
            return this.editRelations.map(editRelation => editRelation.id).indexOf(sectionRelation.id);
        },
        changeInstitution() {
            this.editRelations = [];
            this.newRelation = new SectionRelation();
        },
        isSavable() {
            if (this.editRelations.length === 0 && this.newRelation.isEmpty()) {
                return false;
            }

            let hasInvalidEdits = this.editRelations.filter(editRelation => !this.isValidDateRange(editRelation)).length > 0;
            if (hasInvalidEdits) {
                return false;
            }

            if (!this.newRelation.isEmpty() && !this.newRelation.isValid()) {
                return false;
            }

            return true;
        },
        isValidDateRange(sectionRelation) {
            if (!sectionRelation.end) {
                return true;
            }

            return this.moment(sectionRelation.end, 'DD.MM.YYYY') >= this.moment(sectionRelation.start, 'DD.MM.YYYY');
        },
        remove(sectionRelation, reload, remove) {
            showConfirm(this.$t('confirm'), this.$t('section.relation.confirm.delete', {'name': sectionRelation.section.name}), () => {
                this.editRelations = this.editRelations.filter(r => r.id !== sectionRelation.id);
                remove(sectionRelation);
                SectionRelationRepository
                    .remove(sectionRelation.id)
                    .then(() => {
                        this.$notify.success('section.relation.delete.success', {'name': sectionRelation.section.name});
                        reload();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(sectionRelation) {
            if (this.isEditable(sectionRelation)) {
                return;
            }

            this.editRelations.push(sectionRelation.toData());
        },
        save(reload) {
            if (!this.newRelation.isEmpty()) {
                this.create(reload);
            }

            if (this.editRelations.length > 0) {
                this.update(reload);
            }
        },
        create(reload) {
            this.pendingCreate = true;
            SectionRelationRepository
                .create(this.institution.id, this.newRelation.section.id, this.newRelation.start, this.newRelation.end)
                .then(() => {
                    this.newRelation = new SectionRelation();
                    this.$notify.success('saveChanges.success');
                    reload();
                })
                .catch(error => {
                    if (error.status === 409) {
                        this.$notify.error('section.relation.saveChanges.conflict', {'name': this.newRelation.section.name});
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
                .finally(() => this.pendingCreate = false);
        },
        update(reload) {
            this.pendingUpdate = true;
            SectionRelationRepository
                .update(this.editRelations)
                .then(() => {
                    this.editRelations = [];
                    this.$notify.success('saveChanges.success');
                    reload();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pendingUpdate = false);
        }
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
    .button_save {
        display: block;
        margin-left: auto;
    }
</style>
